import { api, TLivenessTransactionStatus } from '@verifime/api-definition';
import { useEffect, useState } from 'react';
import { toDisplayableCapture } from './toDisplayableCapture';

export type TUseBiometricEvidenceParams = {
  customerId?: string;
  /**
   * Whether to gather the evidence at all. The view sets this when the dialog is wanted.
   *
   * The dialog's data is three calls beyond what the cards need, and most customers a client opens
   * are never clicked through to, so gathering it eagerly spends those calls on nothing. Expiry is
   * not the reason: presigned URLs last an hour, far longer than the gap between opening a customer
   * and opening their evidence.
   */
  enabled?: boolean;
  /** Called once per failed call. This package has no logging surface of its own. */
  onError?: (error: unknown) => void;
};

/**
 * Everything the evidence dialog draws, with each field present only when it is genuinely available.
 *
 * **Presence is the whole contract.** A field is absent when its call has not run, has not returned,
 * or failed, and the dialog renders a control only where its data exists: the play control appears
 * only when there is a video, and the same rule governs the rest. That is deliberately not a
 * loading state per panel, which would need three more state machines for a dialog the client opens
 * only after the cards have already settled.
 */
export type TUseBiometricEvidence = {
  /** True until every call has settled, whether it succeeded or failed. */
  isLoading: boolean;
  /**
   * A `blob:` object URL, not the endpoint's own presigned S3 URL: the portal's `img-src` policy
   * allows the former and blocks the latter (verifime-portal-web#2047). Falls back to the raw URL,
   * unconverted, when it could not be fetched, so the dialog's existing failure tile still fires.
   * See `toDisplayableCapture`.
   */
  faceCaptureUrl?: string;
  /**
   * Unlike the two capture fields either side of it, this reaches the dialog exactly as person-api
   * returns it. No media directive restricts video, so there is nothing here for
   * `toDisplayableCapture` to do.
   */
  faceVideoUrl?: string;
  /** Same conversion as `faceCaptureUrl`, and the same fallback on a failed fetch. */
  documentPortraitUrl?: string;
  similarityScore?: number;
  liveness?: TLivenessTransactionStatus;
  /** When the customer finished the capture, from the liveness transaction. */
  completedOn?: string;
};

const NOTHING_GATHERED: TUseBiometricEvidence = { isLoading: true };

/**
 * Gathers the evidence behind the biometric dialog from the four endpoints that hold it.
 *
 * **Each call lands on its own.** They are not awaited together, so one failure costs only its own
 * field: a client who can still see the findings and the provenance is better served than one shown
 * an empty dialog because a capture URL had expired. This is the page's partial-failure rule applied
 * inside the dialog.
 */
export function useBiometricEvidence({
  customerId,
  enabled = true,
  onError,
}: TUseBiometricEvidenceParams): TUseBiometricEvidence {
  const [evidence, setEvidence] = useState<TUseBiometricEvidence>(NOTHING_GATHERED);

  useEffect(() => {
    // Reset before anything else, including on the early return. `absorb` merges into the current
    // value, so without this any field the new customer's calls do not supply keeps the previous
    // customer's. It leaks only when a call fails, because a successful empty response spreads
    // undefined over the key and clears it, which is what makes it easy to miss: the dialog would
    // render one customer's face under another customer's name, settled and with no warning.
    setEvidence(NOTHING_GATHERED);

    if (!customerId || !enabled) {
      return;
    }

    let abandoned = false;
    // Every object URL a capture conversion below creates, released together on cleanup. Tracked
    // as a list rather than one per field: the two capture fields are independent and either, both
    // or neither may have created one by the time the customer changes or the dialog closes.
    const releases: Array<() => void> = [];

    const absorb = (patch: Partial<TUseBiometricEvidence>) => {
      if (!abandoned) {
        setEvidence((current) => ({ ...current, ...patch }));
      }
    };

    const report = (error: unknown) => {
      if (!abandoned) {
        onError?.(error);
      }
    };

    const gather = <TResponse>(
      call: Promise<TResponse>,
      take: (response: TResponse) => Partial<TUseBiometricEvidence>,
    ) => call.then((response) => absorb(take(response ?? ({} as TResponse)))).catch(report);

    /**
     * Converts an already-resolved response's capture field through `toDisplayableCapture` and
     * folds the result in. Takes the response rather than the call itself so it can be chained
     * after a `gather`-style step that absorbs the same response's other fields first (see the
     * face-check call below): both steps then share one `.catch(report)`, keeping "once per failed
     * call" true even though one call now feeds two independent pieces of state.
     */
    const gatherCapture = <TResponse>(
      response: TResponse,
      take: (response: TResponse) => string | undefined,
      patch: (src?: string) => Partial<TUseBiometricEvidence>,
    ) =>
      toDisplayableCapture(take(response)).then((displayable) => {
        if (abandoned) {
          // The customer changed, or the dialog closed, before the conversion landed. Nothing
          // should hold a blob open for evidence nobody is looking at any more.
          displayable.release();
          return;
        }
        releases.push(displayable.release);
        absorb(patch(displayable.src));
      });

    const settled = Promise.all([
      api
        .getV2personCustomerIdlivenesstransactionlatest({ params: { customerId } })
        .then((r) => {
          const response = r ?? {};
          // Liveness and the completion date absorb immediately: they need no conversion, and
          // holding them back for the capture's fetch to finish would delay two unrelated fields
          // for a reason the dialog was not designed around.
          absorb({ liveness: response.status, completedOn: response.checkedDateTime });
          return gatherCapture(
            response,
            (res) => res.portraitImageUrl,
            (src) => ({ faceCaptureUrl: src }),
          );
        })
        .catch(report),
      gather(
        api.getV2personCustomerIdlivenesstransactionlatestvideo({ params: { customerId } }),
        (r) => ({ faceVideoUrl: r?.videoUrl }),
      ),
      gather(api.getV2personCustomerIdportraitcomparison({ params: { customerId } }), (r) => ({
        similarityScore: r?.similarity,
      })),
      api
        .getV1personIdportrait({ params: { id: customerId } })
        .then((r) =>
          gatherCapture(
            r ?? {},
            (res) => res.portraitUrl,
            (src) => ({ documentPortraitUrl: src }),
          ),
        )
        .catch(report),
    ]);

    // `Promise.all` over calls that each already caught their own rejection, so this resolves once
    // every call has settled and never rejects. It exists only to clear the loading flag.
    settled.then(() => absorb({ isLoading: false }));

    return () => {
      abandoned = true;
      releases.forEach((release) => release());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, enabled]);

  return evidence;
}
